// apps/web/src/components/ui/Toast.tsx
//
// Lightweight toast. Subscribe to a global event bus so any
// component can call `showToast('Saved.', 'success')` without
// threading a prop or context.

import { useEffect, useState } from 'react';

export type ToastVariant = 'info' | 'success' | 'warning' | 'error';

export interface ToastEvent {
  message: string;
  variant?: ToastVariant;
  /** Auto-dismiss after this many ms. Default 5000. Set to 0 to require manual close. */
  ttlMs?: number;
}

const TOAST_EVENT = 'bumbum:toast';

export function showToast(message: string, variant: ToastVariant = 'info', ttlMs = 5000): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<ToastEvent>(TOAST_EVENT, {
    detail: { message, variant, ttlMs },
  }));
}

interface VisibleToast extends ToastEvent {
  id: number;
}

const variantClasses: Record<ToastVariant, string> = {
  info: 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200',
  success: 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200',
  warning: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200',
  error: 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200',
};

let nextId = 0;

export function ToastViewport(): JSX.Element {
  const [toasts, setToasts] = useState<VisibleToast[]>([]);

  useEffect(() => {
    const onToast = (ev: Event) => {
      const detail = (ev as CustomEvent<ToastEvent>).detail;
      const id = ++nextId;
      setToasts((prev) => [...prev, { id, ...detail }]);
      const ttl = detail.ttlMs ?? 5000;
      if (ttl > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, ttl);
      }
    };
    window.addEventListener(TOAST_EVENT, onToast as EventListener);
    return () => window.removeEventListener(TOAST_EVENT, onToast as EventListener);
  }, []);

  if (toasts.length === 0) return <></>;
  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          role={t.variant === 'error' ? 'alert' : 'status'}
          className={`pointer-events-auto min-w-[200px] max-w-sm rounded border p-3 text-sm shadow ${variantClasses[t.variant ?? 'info']}`}
        >
          {t.message}
        </div>
      ))}
    </div>
  );
}
